import { useMemo, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Block, Edit, Groups, OpenInNew, Restore, UploadFile } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { ProductRouteGuard } from '@/components/ProductRouteGuard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as recipients from '@/api/recipients';
import type { ImportReceiverItem, Receiver, SaleCondition, TaxIdKind } from '@/api/recipients';
import { listCuitProfiles } from '@/api/cuitProfiles';
import { toApiError } from '@/api/client';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { ReceiverDialog } from '@/components/ReceiverDialog';
import { useCurrentCuit } from '@/auth/CuitContext';
import { formatCurrency, formatCuit } from '@/lib/formatters';
import { IVA_CONDITION_LABEL } from '@/lib/labels';
import { parseArcaRecipients } from '@/lib/arcaRecipientImport';

export const Route = createFileRoute('/_authenticated/receptores/')({
  component: ReceptoresRoute,
});

const TAX_ID_KIND: Record<TaxIdKind, string> = {
  Cuit: 'CUIT',
  Cuil: 'CUIL',
  Cdi: 'CDI',
  CiExtranjera: 'CI Extranjera',
  Dni: 'DNI',
  Passport: 'Pasaporte',
  ConsumidorFinalSinDocumento: 'Consumidor final',
};
const SALE_CONDITION: Record<SaleCondition, string> = { Contado: 'Contado', CuentaCorriente: 'Cuenta corriente' };

type Toast = { message: string; severity: 'success' | 'error' };


function ReceptoresRoute(): JSX.Element {
  return (
    <ProductRouteGuard
      products={['Estudio', 'Empresa']}
      requirePermission="canViewInvoices"
      title="Cuenta corriente no disponible"
      description="Seleccioná un producto fiscal con permisos para ver comprobantes antes de abrir la cartera."
    >
      <ReceptoresPage />
    </ProductRouteGuard>
  );
}

function ReceptoresPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { currentCuitProfileId, setCurrentCuitProfile } = useCurrentCuit();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Receiver | 'new' | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const profilesQ = useQuery({
    queryKey: ['cuit-profiles', 'recipients'],
    queryFn: () => listCuitProfiles(0, 200),
  });
  const activeProfiles = (profilesQ.data?.items ?? []).filter((profile) => profile.status === 'Active');
  const selectedProfileId =
    activeProfiles.some((profile) => profile.id === currentCuitProfileId)
      ? currentCuitProfileId!
      : activeProfiles[0]?.id ?? '';
  const selectedProfile = activeProfiles.find((profile) => profile.id === selectedProfileId);

  const listQ = useQuery({
    queryKey: ['recipients', selectedProfileId],
    queryFn: () => recipients.listRecipients(selectedProfileId, 0, 200),
    enabled: Boolean(selectedProfileId),
  });
  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: ['recipients', selectedProfileId] });
  const onError = (err: unknown): void => setToast({ message: toApiError(err).message, severity: 'error' });

  const toggleMut = useMutation({
    mutationFn: ({ r }: { r: Receiver }) => {
      if (!r.cuitProfileId) throw new Error('Este cliente no tiene un CUIT emisor asignado.');
      return r.status === 'Active'
        ? recipients.deactivateRecipient(r.id, r.cuitProfileId)
        : recipients.reactivateRecipient(r.id, r.cuitProfileId).then(() => undefined);
    },
    onSuccess: () => { invalidate(); setToast({ message: 'Cliente actualizado.', severity: 'success' }); },
    onError,
  });

  const all = listQ.data?.items ?? [];
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) => r.legalName.toLowerCase().includes(q) || r.taxId.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
  }, [all, search]);

  const withDebt = all.filter((r) => r.outstandingBalance > 0);

  if (profilesQ.isLoading || listQ.isLoading) return <LoadingScreen message="Cargando clientes…" />;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Groups color="primary" /> Cuenta corriente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Clientes, saldos pendientes y condiciones de pago en un solo lugar.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            select
            size="small"
            label="CUIT emisor"
            value={selectedProfileId}
            sx={{ minWidth: 300 }}
            onChange={(event) => setCurrentCuitProfile(event.target.value)}
          >
            {activeProfiles.map((profile) => (
              <MenuItem key={profile.id} value={profile.id}>
                {profile.razonSocial} · {formatCuit(profile.cuit)}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            disabled={!selectedProfileId}
            onClick={() => setImportOpen(true)}
          >
            Importar CSV ARCA
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            disabled={!selectedProfileId}
            onClick={() => setEditing('new')}
          >
            Nuevo cliente
          </Button>
        </Stack>
      </Stack>

      <MuiAlert severity="info" sx={{ mb: 3 }} action={
        <Button component="a" href="https://www.arca.gob.ar/monotributo/" target="_blank" rel="noreferrer" size="small" endIcon={<OpenInNew />}>
          Abrir CCMA en ARCA
        </Button>
      }>
        Esta es la cartera comercial exclusiva de <strong>{selectedProfile?.razonSocial ?? 'este CUIT'}</strong>.
        Sus clientes, recurrencias y saldos no se mezclan con otros CUITs del espacio. La cuenta
        corriente tributaria CCMA se consulta dentro de ARCA con clave fiscal.
      </MuiAlert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Kpi label="Clientes" value={String(all.length)} hint={`${all.filter((r) => r.status === 'Active').length} activos`} />
        <Kpi label="Con saldo" value={String(withDebt.length)} hint="cuenta corriente" />
      </Grid>

      <TextField size="small" placeholder="Buscar por nombre, CUIT o email" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2, minWidth: 320 }} />

      {listQ.error ? (
        <MuiAlert severity="error">{toApiError(listQ.error).message}</MuiAlert>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Groups fontSize="inherit" />}
          title={all.length === 0 ? 'Sin clientes' : 'Sin resultados'}
          description={all.length === 0 ? 'Registrá tus clientes para emitirles comprobantes y llevar su cuenta corriente.' : 'Probá con otro término de búsqueda.'}
          actionLabel={all.length === 0 ? 'Nuevo cliente' : undefined}
          onAction={all.length === 0 ? () => setEditing('new') : undefined}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Condición IVA</TableCell>
                <TableCell>Cond. venta</TableCell>
                <TableCell align="right">Saldo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.legalName}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {TAX_ID_KIND[r.taxIdKind]} {r.taxIdKind === 'Cuit' ? formatCuit(r.taxId) : r.taxId}{r.email ? ` · ${r.email}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>{IVA_CONDITION_LABEL[r.ivaCondition] ?? r.ivaCondition}</TableCell>
                  <TableCell>{SALE_CONDITION[r.defaultSaleCondition]}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: r.outstandingBalance > 0 ? 600 : 400, color: r.outstandingBalance > 0 ? 'warning.main' : 'text.secondary' }}>
                    {formatCurrency(r.outstandingBalance, 'ARS')}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={r.status === 'Active' ? 'success' : 'default'} label={r.status === 'Active' ? 'Activo' : 'Inactivo'} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditing(r)}><Edit fontSize="small" /></IconButton></Tooltip>
                      {r.status === 'Active' ? (
                        <Tooltip title="Desactivar"><span><IconButton size="small" color="error" disabled={toggleMut.isPending} onClick={() => toggleMut.mutate({ r })}><Block fontSize="small" /></IconButton></span></Tooltip>
                      ) : (
                        <Tooltip title="Reactivar"><span><IconButton size="small" color="success" disabled={toggleMut.isPending} onClick={() => toggleMut.mutate({ r })}><Restore fontSize="small" /></IconButton></span></Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {importOpen && (
        <ReceiverImportDialog
          cuitProfileId={selectedProfileId}
          onClose={() => setImportOpen(false)}
          onImported={(count) => { setImportOpen(false); invalidate(); setToast({ message: `${count} clientes importados.`, severity: 'success' }); }}
          onError={onError}
        />
      )}

      {editing && (
        <ReceiverDialog
          cuitProfileId={selectedProfileId}
          receiver={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate(); setToast({ message: 'Cliente guardado en la cartera del CUIT.', severity: 'success' }); }}
          onError={onError}
        />
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">{toast?.message}</MuiAlert>
      </Snackbar>
    </Box>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }): JSX.Element {
  return (
    <Grid item xs={6} sm={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h5">{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{hint}</Typography>
      </Paper>
    </Grid>
  );
}

function ReceiverImportDialog({ cuitProfileId, onClose, onImported, onError }: { cuitProfileId: string; onClose: () => void; onImported: (count: number) => void; onError: (err: unknown) => void }): JSX.Element {
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<ImportReceiverItem[]>([]);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const readFile = async (file?: File): Promise<void> => {
    setCsv(''); setPreview([]); setError('');
    if (!file) return;
    if (file.size > 1_000_000) { setError('El archivo no puede superar 1 MB.'); return; }
    setBusy(true);
    try {
      const bytes = await file.arrayBuffer();
      let text: string;
      try { text = new TextDecoder('utf-8', { fatal: true }).decode(bytes); }
      catch { text = new TextDecoder('windows-1252').decode(bytes); }
      setCsv(text);
    } catch { setError('No se pudo leer el archivo.'); }
    finally { setBusy(false); }
  };

  const review = (): void => {
    setError(''); setPreview([]);
    try { setPreview(parseArcaRecipients(csv)); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'Archivo inválido.'); }
  };

  const save = async (): Promise<void> => {
    setBusy(true); setError('');
    try {
      const imported = await recipients.importRecipients(cuitProfileId, preview, updateExisting);
      onImported(imported.length);
    } catch (err) { onError(err); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Importar clientes desde CSV de ARCA</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Usá el diseño oficial de clientes del Facturador Móvil: CODIGO, CONDICION_IVA,
            TIPO_DOCUMENTO, NRO_DOCUMENTO, APELLIDO_NOMBRE_RAZON, DOMICILIO y EMAIL.
          </Typography>
          <TextField
            type="file"
            label="Archivo CSV"
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { accept: '.csv,text/csv' } }}
            disabled={busy}
            onChange={e => void readFile((e.target as HTMLInputElement).files?.[0])}
          />
          <FormControlLabel control={<Switch checked={updateExisting} disabled={busy} onChange={e => setUpdateExisting(e.target.checked)} />} label="Actualizar clientes existentes por documento" />
          {error && <MuiAlert severity="error">{error}</MuiAlert>}
          <Button onClick={review} disabled={busy || !csv}>Revisar importación</Button>
          {preview.length > 0 && (
            <>
              <MuiAlert severity="info">
                {preview.length} clientes listos. {updateExisting ? 'Los documentos existentes se actualizarán sin tocar saldos.' : 'Los documentos existentes rechazan todo el lote.'}
              </MuiAlert>
              <Stack spacing={1} sx={{ maxHeight: 280, overflow: 'auto' }}>
                {preview.map(item => (
                  <Typography key={item.code} variant="body2">
                    {item.code} · {item.legalName} · {TAX_ID_KIND[item.taxIdKind]} {item.taxId} · {IVA_CONDITION_LABEL[item.ivaCondition] ?? item.ivaCondition}{item.email ? ` · ${item.email}` : ''}
                  </Typography>
                ))}
              </Stack>
              <Button variant="contained" disabled={busy} onClick={() => void save()}>Importar {preview.length} clientes</Button>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={busy}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}
